import React, { useCallback, useMemo } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import Button from "@dtci/spellbook/Button";
import { CustomHeader } from "tap/components/AGGrid/cell-renderers/CustomHeader.jsx";
import { EditableDropdownRenderer } from "tap/components/AGGrid/cell-renderers/EditableDropdown.jsx";
import { EditableDateRenderer } from "tap/components/AGGrid/cell-renderers/EditableDate.jsx";
import { EditableTextRenderer } from "tap/components/AGGrid/cell-renderers/EditableText.jsx";
import { EditableNumberRenderer } from "tap/components/AGGrid/cell-renderers/EditableNumber.jsx";
import { EditableMultiSelectRenderer } from "tap/components/AGGrid/cell-renderers/EditableMultiSelect.jsx";
import { RIGHTSLINE_URL } from "../../../../../config/constants";

/** Statuses that make an offer non-editable */
const NON_EDITABLE_STATUSES = [
  "TO_DELETE",
  "INACTIVE_DEAL",
  "BLANK_DEAL_FIELDS",
];

/** Static dropdown options */
const READY_TO_PUBLISH_OPTIONS = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

const OFFER_STATUS_OPTIONS = [
  { label: "Offered", value: "Offered" },
  { label: "Taken", value: "Taken" },
  { label: "Pass", value: "Pass" },
  { label: "Put", value: "Put" },
  { label: "Cancelled", value: "Cancelled" },
];

const AVAILS_TYPE_OVERRIDE_OPTIONS = [
  { label: "Feature", value: "Feature" },
  { label: "TV", value: "TV" },
];

/**
 * Check if an offer row is editable based on tapOfferStatus and TPR licensing job
 */
const isOfferEditable = (data, isTprJob) => {
  if (isTprJob) {
    return false;
  }
  const tapOfferStatus = data?.tapOfferStatus;
  return !NON_EDITABLE_STATUSES.includes(tapOfferStatus);
};

/**
 * Validation function for history button.
 * Only show if modifiedAt > createdAt (row has been modified after creation).
 */
const hasBeenModified = (data) => {
  const createdAt = data?.createdAt ? moment(data.createdAt) : null;
  const modifiedAt = data?.modifiedAt ? moment(data.modifiedAt) : null;
  return modifiedAt && createdAt && modifiedAt.isAfter(createdAt);
};

/** Fixed-width slot for action buttons to maintain alignment */
const ActionSlot = ({ width, children }) => (
  <div
    style={{
      width: `${width}px`,
      minWidth: `${width}px`,
      display: "flex",
      justifyContent: "center",
    }}
  >
    {children}
  </div>
);

/**
 * Unified Row Actions Cell Renderer
 * Shows all action buttons: Edit/Save/Cancel, Push/Update, History, Delete
 * Uses fixed-width slots to keep buttons aligned across rows
 */
const RowActionsCellRenderer = (
  onEditOffer,
  onSaveOffer,
  onCancelEdit,
  getEditingRows,
  getEditedValues,
  onDeleteOffer,
  onPushOrUpdateOffer,
  getOfferHistoryHref,
  shouldRenderDelete,
  shouldRenderPush,
  isTprJob,
) => {
  const Renderer = ({ data }) => {
    const editingRows = getEditingRows ? getEditingRows() : {};
    const isEditing = editingRows[data.id];
    const editable = isOfferEditable(data, isTprJob);
    const canDelete = shouldRenderDelete ? shouldRenderDelete(data) : true;
    const canPush = shouldRenderPush ? shouldRenderPush(data) : true;
    const showHistory = hasBeenModified(data);
    const historyHref = showHistory ? getOfferHistoryHref(data) : null;

    // Determine Push or Update
    const isUpdate =
      data?.offerTableRowRlId !== null && data?.offerTableRowRlId !== undefined;
    const pushIcon = isUpdate ? "fa-arrows-rotate" : "fa-upload";
    const pushText = isUpdate ? "Update" : "Push";

    // Memoized handlers to prevent unnecessary re-renders
    const handleDelete = useCallback(() => onDeleteOffer(data), [data]);
    const handleEdit = useCallback(() => onEditOffer(data), [data]);
    const handleSave = useCallback(() => onSaveOffer(data), [data]);
    const handleCancel = useCallback(() => onCancelEdit(data), [data]);
    const handlePush = useCallback(() => onPushOrUpdateOffer(data), [data]);

    // Memoized container style
    const containerStyle = useMemo(
      () => ({ display: "flex", gap: "8px", alignItems: "center" }),
      [],
    );

    return (
      <div style={containerStyle}>
        {/* Delete - 32px slot */}
        <ActionSlot width={32}>
          {canDelete && (
            <Button
              iconType="fa-regular fa-trash"
              type="button"
              styleType="link"
              className="delete-btn sb-typography sb-typography_T20"
              onClick={handleDelete}
            />
          )}
        </ActionSlot>

        {/* Edit / Save - 32px slot */}
        <ActionSlot width={32}>
          {editable && !isEditing && (
            <Button
              iconType="fa-regular fa-pen-to-square"
              type="button"
              styleType="link"
              className="sb-typography sb-typography_T20"
              onClick={handleEdit}
            />
          )}
          {editable && isEditing && (
            <Button
              className="save-btn"
              style={{ color: "#16a34a" }}
              iconType="fa-regular fa-floppy-disk"
              type="button"
              styleType="link"
              onClick={handleSave}
            />
          )}
          {!editable && (
            <span style={{ color: "#999", fontSize: "10px" }}>N/A</span>
          )}
        </ActionSlot>

        {/* Cancel (only in edit mode) - 32px slot */}
        <ActionSlot width={32}>
          {editable && isEditing && (
            <Button
              className="cancel-edit-btn"
              iconType="fa-regular fa-xmark"
              type="button"
              styleType="link"
              onClick={handleCancel}
            />
          )}
        </ActionSlot>

        {/* Push/Update - 80px slot */}
        <ActionSlot width={80}>
          {canPush && (
            <Button
              iconType={`fa-regular ${pushIcon}`}
              type="button"
              styleType="link"
              className="sb-typography sb-typography_T20"
              onClick={handlePush}
            >
              {pushText}
            </Button>
          )}
        </ActionSlot>

        {/* History - 32px slot (Link for open-in-new-tab) */}
        <ActionSlot width={32}>
          {historyHref ? (
            <Link
              to={historyHref}
              className="sb-typography sb-typography_T20"
              style={{ color: "#3611c8" }}
              aria-label="Offer history"
              onContextMenu={(e) => e.stopPropagation()}
            >
              <i className="fa-regular fa-clock-rotate-left" />
            </Link>
          ) : null}
        </ActionSlot>
      </div>
    );
  };

  return Renderer;
};

/**
 * Format TAP Offer Status for display
 */
const formatTapOfferStatus = (status) => {
  switch (status) {
    case "PENDING":
      return "Pending";
    case "PUSHED":
    case "OFFER_IN_PAST_SYSTEM_UPDATED":
      return "Pushed";
    case "DUPLICATE":
    case "DUPLICATE_DIFFJOB":
      return "Duplicate";
    case "SYSTEM_UPDATED":
      return "System Updated";
    case "USER_MODIFIED":
      return "User Modified";
    case "SYSTEM_UPDATED_OFFER_ACTIVE":
      return "Offer Active - System Updated";
    case "USER_MODIFIED_OFFER_ACTIVE":
      return "Offer Active - User Modified";
    case "INACTIVE_DEAL":
      return "Inactive Deal";
    case "BLANK_DEAL_FIELDS":
      return "Blank Deal Fields";
    case "RIGHTS_ISSUE":
      return "Rights Issue";
    case "MISSING_PRICING":
      return "Missing Pricing";
    case "TO_DELETE":
      return "To Delete";
    case "SYSTEM_CREATED":
      return "System Created";
    case "INFO_RESTRICTIONS":
      return "Info Restrictions";
    default:
      return status;
  }
};

// ============ ACTION COLUMNS ============

/**
 * Delete Cell Renderer
 * Shows a red trash icon button for deleting offers
 */
const DeleteCellRenderer = (onDeleteOffer, shouldRenderDelete) => {
  const Renderer = ({ data }) => {
    if (shouldRenderDelete && !shouldRenderDelete(data)) {
      return null;
    }

    return (
      <Button
        iconType="fa-regular fa-trash"
        type="button"
        styleType="link"
        className="delete-btn sb-typography sb-typography_T20 sb-typography_truncate"
        onClick={() => onDeleteOffer(data)}
      />
    );
  };

  return Renderer;
};

export const deleteOffer = (onDeleteOffer, shouldRenderDelete) => ({
  field: "",
  headerName: "",
  cellRenderer: DeleteCellRenderer(onDeleteOffer, shouldRenderDelete),
  minWidth: 50,
});

/**
 * Push/Update Cell Renderer
 * Shows different icons and text based on whether the offer has been pushed before:
 * - New push: Upload icon
 * - Update: Refresh/sync icon
 */
const PushOrUpdateCellRenderer = (onPushOrUpdateOffer, shouldRenderPush) => {
  const Renderer = ({ data }) => {
    // Check if should render
    if (shouldRenderPush && !shouldRenderPush(data)) {
      return null;
    }

    const isUpdate =
      data?.offerTableRowRlId !== null && data?.offerTableRowRlId !== undefined;
    const icon = isUpdate ? "fa-arrows-rotate" : "fa-upload";
    const text = isUpdate ? "Update" : "Push";

    return (
      <Button
        style={{ color: "#3611c8" }}
        iconType={`fa-regular ${icon}`}
        type="button"
        styleType="link"
        className="sb-typography sb-typography_T20 sb-typography_truncate"
        onClick={() => onPushOrUpdateOffer(data)}
      >
        {text}
      </Button>
    );
  };

  return Renderer;
};

export const pushOrUpdate = (onPushOrUpdateOffer, shouldRenderPush) => ({
  field: "",
  headerName: "",
  cellRenderer: PushOrUpdateCellRenderer(onPushOrUpdateOffer, shouldRenderPush),
  minWidth: 130,
});

const HistoryLinkCellRenderer = (getOfferHistoryHref) => {
  const Renderer = ({ data }) => {
    if (!hasBeenModified(data)) return null;
    const to = getOfferHistoryHref(data);
    if (!to) return null;
    return (
      <Link
        to={to}
        state={{ fromOffers: true }}
        className="sb-typography sb-typography_T20"
        style={{ color: "#3611c8" }}
        aria-label="Offer history"
        onContextMenu={(e) => e.stopPropagation()}
      >
        <i className="fa-regular fa-clock-rotate-left" />
      </Link>
    );
  };
  return Renderer;
};

export const history = (getOfferHistoryHref) => ({
  field: "",
  headerName: "",
  cellRenderer: HistoryLinkCellRenderer(getOfferHistoryHref),
  minWidth: 50,
});

export const id = {
  field: "id",
  headerName: "ID",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "id",
    page: "offerTable",
  },
  minWidth: 80,
  sortable: true,
  comparator: (a, b) => Number(a) - Number(b),
};

export const rowActions = (
  onEditOffer,
  onSaveOffer,
  onCancelEdit,
  getEditingRows,
  getEditedValues,
  onDeleteOffer,
  onPushOrUpdateOffer,
  getOfferHistoryHref,
  shouldRenderDelete,
  shouldRenderPush,
  isTprJob = false,
) => ({
  field: "",
  headerName: "Actions",
  cellRenderer: RowActionsCellRenderer(
    onEditOffer,
    onSaveOffer,
    onCancelEdit,
    getEditingRows,
    getEditedValues,
    onDeleteOffer,
    onPushOrUpdateOffer,
    getOfferHistoryHref,
    shouldRenderDelete,
    shouldRenderPush,
    isTprJob,
  ),
  pinned: "left",
  lockPosition: true,
  suppressMovable: true,
  minWidth: 250,
});

// ============ DISPLAY-ONLY COLUMNS ============

export const createdAt = {
  field: "createdAt",
  headerName: "Created",
  valueFormatter: ({ value }) =>
    value
      ? moment(moment.utc(value).toDate())
          .tz(moment.tz.guess())
          .format("DD-MMM-YYYY, h:mm:ss A z")
      : "",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "createdAt",
    page: "offerTable",
  },
  minWidth: 250,
};

export const needsAudit = {
  field: "needsAudit",
  headerName: "Needs Audit",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "needsAudit",
    page: "offerTable",
  },
  minWidth: 100,
  cellRenderer: ({ value }) => {
    if (value === "true" || value === true) {
      return <i className="fa-regular fa-eye" style={{ color: "blue" }}></i>;
    }
    return null;
  },
};

export const tapOfferStatus = {
  field: "tapOfferStatus",
  headerName: "TAP Offer Status",
  valueFormatter: ({ value }) => formatTapOfferStatus(value),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "tapOfferStatus",
    page: "offerTable",
  },
  minWidth: 200,
};

export const offerTableRowRlId = {
  field: "offerTableRowRlId",
  headerName: "RL ID",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "offerTableRowRlId",
    page: "offerTable",
  },
  minWidth: 100,
};

export const licensingJobId = {
  field: "licensingJobId",
  headerName: "Licensing Job ID",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "licensingJobId",
    page: "offerTable",
  },
  minWidth: 150,
};

export const dealId = {
  field: "dealId",
  headerName: "Deal ID",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "dealId",
    page: "offerTable",
  },
  minWidth: 100,
  cellRenderer: ({ value }) => {
    if (!value) return null;
    const url = RIGHTSLINE_URL("deal");
    return (
      <a
        href={`${url}${value}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#4f46e5" }}
      >
        {value}
      </a>
    );
  },
};

export const dealName = {
  field: "dealName",
  headerName: "Deal Name",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "dealName",
    page: "offerTable",
  },
  minWidth: 400,
};

export const catalogId = {
  field: "catalogId",
  headerName: "Catalog ID",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "catalogId",
    page: "offerTable",
  },
  minWidth: 125,
  cellRenderer: ({ value }) => {
    if (!value) return null;
    const url = RIGHTSLINE_URL("catalog");
    return (
      <a
        href={`${url}${value}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#4f46e5" }}
      >
        {value}
      </a>
    );
  },
};

export const catalogName = {
  field: "catalogName",
  headerName: "Catalog Name",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "catalogName",
    page: "offerTable",
  },
  minWidth: 400,
};

export const media = {
  field: "media",
  headerName: "Media",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "media",
    page: "offerTable",
  },
  minWidth: 200,
};

export const territory = {
  field: "territory",
  headerName: "Territory",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "territory",
    page: "offerTable",
  },
  minWidth: 200,
};

export const languages = {
  field: "languages",
  headerName: "Languages",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "languages",
    page: "offerTable",
  },
  minWidth: 200,
  valueFormatter: ({ value }) =>
    value ? String(value).replaceAll("|", " | ") : "",
};

export const format = {
  field: "format",
  headerName: "Format",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "format",
    page: "offerTable",
  },
  minWidth: 140,
};

export const rateCardType = {
  field: "rateCardType",
  headerName: "Rate Card Type",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "rateCardType",
    page: "offerTable",
  },
  minWidth: 180,
};

export const rateCardId = {
  field: "rateCardId",
  headerName: "Rate Card ID",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "rateCardId",
    page: "offerTable",
  },
  minWidth: 130,
};

export const rateCardName = {
  field: "rateCardName",
  headerName: "Rate Card Name",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "rateCardName",
    page: "offerTable",
  },
  minWidth: 400,
};

export const windows = {
  field: "windows",
  headerName: "Window",
  valueFormatter: ({ value }) => {
    if (typeof value === "string") return value;
    if (Array.isArray(value))
      return value.map((w) => w.windowName || w.label || w).join(", ");
    if (typeof value === "object" && value !== null)
      return value.windowName || value.label || "";
    return value ?? "";
  },
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "windows",
    page: "offerTable",
  },
  minWidth: 200,
};

export const windowNum = {
  field: "windowNum",
  headerName: "Window Number",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "windowNum",
    page: "offerTable",
  },
  minWidth: 150,
};

export const pmxPlatform = {
  field: "pmxPlatform",
  headerName: "PMX Platform",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "pmxPlatform",
    page: "offerTable",
  },
  minWidth: 160,
};

export const currency = {
  field: "currency",
  headerName: "Currency",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "currency",
    page: "offerTable",
  },
  minWidth: 140,
};

export const emaPriceType = {
  field: "emaPriceType",
  headerName: "EMA Price Type",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "emaPriceType",
    page: "offerTable",
  },
  minWidth: 140,
};

export const episodeEmaPriceType = {
  field: "episodeEmaPriceType",
  headerName: "Episode EMA Price Type",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "episodeEmaPriceType",
    page: "offerTable",
  },
  minWidth: 200,
};

export const viewingPeriod = {
  field: "viewingPeriod",
  headerName: "Viewing Period",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "viewingPeriod",
    page: "offerTable",
  },
  minWidth: 160,
};

export const ocad = {
  field: "ocad",
  headerName: "Consumer Announcement Date",
  valueFormatter: ({ value }) =>
    value ? moment(value).format("DD-MMM-YYYY") : "",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "ocad",
    page: "offerTable",
  },
  minWidth: 200,
};

export const suppressionLiftDate = {
  field: "suppressionLiftDate",
  headerName: "Suppression Lift Date",
  valueFormatter: ({ value }) =>
    value ? moment(value).format("DD-MMM-YYYY") : "",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "suppressionLiftDate",
    page: "offerTable",
  },
  minWidth: 200,
};

export const exclusivity = {
  field: "exclusivity",
  headerName: "Exclusivity",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "exclusivity",
    page: "offerTable",
  },
  minWidth: 160,
};

export const holdbackLanguages = {
  field: "holdbackLanguages",
  headerName: "Holdback Languages",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "holdbackLanguages",
    page: "offerTable",
  },
  minWidth: 200,
  valueFormatter: ({ value }) =>
    value ? String(value).replaceAll("|", " | ") : "",
};

export const languageAvailsDisplay = {
  field: "languageAvailsDisplay",
  headerName: "Language Avails Display",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "languageAvailsDisplay",
    page: "offerTable",
  },
  minWidth: 160,
};

export const informationalRestrictions = {
  field: "informationalRestrictions",
  headerName: "Informational Restrictions",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "informationalRestrictions",
    page: "offerTable",
  },
  minWidth: 300,
};

export const message = {
  field: "message",
  headerName: "Message",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "message",
    page: "offerTable",
  },
  minWidth: 500,
};

// ============ EDITABLE COLUMNS ============

export const readyToPublish = (getEditingRows, onValueChange) => ({
  field: "readyToPublish",
  headerName: "Ready to Publish",
  cellRenderer: EditableDropdownRenderer(
    READY_TO_PUBLISH_OPTIONS,
    getEditingRows,
    onValueChange,
    "readyToPublish",
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "readyToPublish",
    page: "offerTable",
  },
  minWidth: 300,
});

export const offerStatus = (getEditingRows, onValueChange) => ({
  field: "offerStatus",
  headerName: "RL Offer Status",
  cellRenderer: EditableDropdownRenderer(
    OFFER_STATUS_OPTIONS,
    getEditingRows,
    onValueChange,
    "offerStatus",
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "offerStatus",
    page: "offerTable",
  },
  minWidth: 300,
});

export const dateTaken = (getEditingRows, onValueChange) => ({
  field: "dateTaken",
  headerName: "Date Taken",
  cellRenderer: EditableDateRenderer(
    getEditingRows,
    onValueChange,
    "dateTaken",
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "dateTaken",
    page: "offerTable",
  },
  minWidth: 160,
});

export const termStart = (getEditingRows, onValueChange) => ({
  field: "termStart",
  headerName: "Term Start",
  cellRenderer: EditableDateRenderer(
    getEditingRows,
    onValueChange,
    "termStart",
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "termStart",
    page: "offerTable",
  },
  minWidth: 160,
});

export const termEnd = (getEditingRows, onValueChange) => ({
  field: "termEnd",
  headerName: "Term End",
  cellRenderer: EditableDateRenderer(getEditingRows, onValueChange, "termEnd"),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "termEnd",
    page: "offerTable",
  },
  minWidth: 160,
});

export const startDateStatus = (getEditingRows, onValueChange) => ({
  field: "startDateStatus",
  headerName: "Start Date Status",
  cellRenderer: EditableTextRenderer(
    getEditingRows,
    onValueChange,
    "startDateStatus",
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "startDateStatus",
    page: "offerTable",
  },
  minWidth: 160,
});

export const endDateStatus = (getEditingRows, onValueChange) => ({
  field: "endDateStatus",
  headerName: "End Date Status",
  cellRenderer: EditableTextRenderer(
    getEditingRows,
    onValueChange,
    "endDateStatus",
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "endDateStatus",
    page: "offerTable",
  },
  minWidth: 160,
});

export const rating = (getEditingRows, onValueChange) => ({
  field: "rating",
  headerName: "Rating",
  cellRenderer: EditableTextRenderer(getEditingRows, onValueChange, "rating"),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "rating",
    page: "offerTable",
  },
  minWidth: 160,
});

export const tier = (getEditingRows, onValueChange) => ({
  field: "tier",
  headerName: "Tier",
  cellRenderer: EditableTextRenderer(getEditingRows, onValueChange, "tier", {
    maxLength: 20,
    pattern: /^[A-Za-z\d\s]*$/, // No special characters
  }),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "tier",
    page: "offerTable",
  },
  minWidth: 140,
});

export const licenseFee = (getEditingRows, onValueChange) => ({
  field: "licenseFee",
  headerName: "License Fee",
  cellRenderer: EditableNumberRenderer(
    getEditingRows,
    onValueChange,
    "licenseFee",
    {
      decimalScale: 2,
    },
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "licenseFee",
    page: "offerTable",
  },
  minWidth: 120,
});

export const minDeemedPrice = (getEditingRows, onValueChange) => ({
  field: "minDeemedPrice",
  headerName: "Min Deemed Price",
  cellRenderer: EditableNumberRenderer(
    getEditingRows,
    onValueChange,
    "minDeemedPrice",
    {
      decimalScale: 2,
    },
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "minDeemedPrice",
    page: "offerTable",
  },
  minWidth: 150,
});

export const seasonPassAmt = (getEditingRows, onValueChange) => ({
  field: "seasonPassAmt",
  headerName: "Season Pass Amt",
  cellRenderer: EditableNumberRenderer(
    getEditingRows,
    onValueChange,
    "seasonPassAmt",
    {
      decimalScale: 2,
    },
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "seasonPassAmt",
    page: "offerTable",
  },
  minWidth: 150,
});

export const episodeTier = (getEditingRows, onValueChange) => ({
  field: "episodeTier",
  headerName: "Episode Tier",
  cellRenderer: EditableTextRenderer(
    getEditingRows,
    onValueChange,
    "episodeTier",
    {
      maxLength: 20,
      pattern: /^[A-Za-z\d\s]*$/,
    },
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "episodeTier",
    page: "offerTable",
  },
  minWidth: 140,
});

export const percentageSplit = (getEditingRows, onValueChange) => ({
  field: "percentageSplit",
  headerName: "Percentage Split",
  cellRenderer: EditableNumberRenderer(
    getEditingRows,
    onValueChange,
    "percentageSplit",
    {
      decimalScale: 2,
      suffix: "%",
    },
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "percentageSplit",
    page: "offerTable",
  },
  minWidth: 150,
});

export const preSell = (getEditingRows, onValueChange) => ({
  field: "preSell",
  headerName: "Pre Sell",
  cellRenderer: EditableDateRenderer(getEditingRows, onValueChange, "preSell"),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "preSell",
    page: "offerTable",
  },
  minWidth: 160,
});

export const subLanguage = (
  getEditingRows,
  onValueChange,
  subDubLanguageOptions,
  onLanguageSelect,
  getLastSelectedLanguages,
) => ({
  field: "subLanguage",
  headerName: "Sub Language",
  cellRenderer: EditableMultiSelectRenderer(
    subDubLanguageOptions || [],
    getEditingRows,
    onValueChange,
    "subLanguage",
    onLanguageSelect,
    getLastSelectedLanguages,
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "subLanguage",
    page: "offerTable",
  },
  minWidth: 400,
});

export const dubLanguage = (
  getEditingRows,
  onValueChange,
  subDubLanguageOptions,
  onLanguageSelect,
  getLastSelectedLanguages,
) => ({
  field: "dubLanguage",
  headerName: "Dub Language",
  cellRenderer: EditableMultiSelectRenderer(
    subDubLanguageOptions || [],
    getEditingRows,
    onValueChange,
    "dubLanguage",
    onLanguageSelect,
    getLastSelectedLanguages,
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "dubLanguage",
    page: "offerTable",
  },
  minWidth: 400,
});

export const tprProgramName = (getEditingRows, onValueChange) => ({
  field: "tprProgramName",
  headerName: "TPR Program Name",
  cellRenderer: EditableTextRenderer(
    getEditingRows,
    onValueChange,
    "tprProgramName",
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "tprProgramName",
    page: "offerTable",
  },
  minWidth: 180,
});

export const availsTypeOverride = (getEditingRows, onValueChange) => ({
  field: "availsTypeOverride",
  headerName: "Avails Type Override",
  cellRenderer: EditableDropdownRenderer(
    AVAILS_TYPE_OVERRIDE_OPTIONS,
    getEditingRows,
    onValueChange,
    "availsTypeOverride",
  ),
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "availsTypeOverride",
    page: "offerTable",
  },
  minWidth: 300,
});
